import React, { useEffect } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { RovoGeneratingAnimation } from '../ai-answer-dialog/ai-answer-loading/rovo-generating-animation';
import { onKnowledgeCardLoadingDialogShown } from '../ai-answer-dialog/analytics';
import { useAnalytics } from '../common/controller/analytics/use-analytics';
import { QueryIntent } from '../constants';
import RovoIcon from '../v2/common/ui/assets/Rovo.svg';

const styles = cssMap({
	containerNewSAINLoading: {
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		height: '198px',
	},
	inline: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		height: '40px',
	},
	agentCardHeightOverride: {
		height: '294px',
	},
});

const messages = defineMessages({
	knowledge_card_loading: {
		id: 'search_ai.knowledge_card_loading',
		defaultMessage: 'Searching across your connected apps',
		description: 'Text for knowledge card loading state',
	},
	knowledge_card_rovo_is_thinking: {
		id: 'search_ai.knowledge_card_rovo_is_thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Text for the knowledge card "Rovo is thinking" loading state',
	},
});

export function KnowledgeCardLoading({
	queryIntent,
}: {
	queryIntent?: QueryIntent;
}): React.JSX.Element {
	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent(onKnowledgeCardLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<AnimatedWrapper>
			<Stack
				xcss={cx(
					styles.containerNewSAINLoading,
					queryIntent === QueryIntent.AGENT && styles.agentCardHeightOverride,
				)}
				space="space.200"
			>
				<Inline space={'space.100'} xcss={styles.inline} alignBlock="center">
					<Image src={RovoIcon} alt="" />
					<Inline>
						<Text color={'color.text.subtle'}>
							<FormattedMessage {...messages.knowledge_card_rovo_is_thinking} />
						</Text>
						<RovoGeneratingAnimation />
					</Inline>
				</Inline>
				<Skeleton width="100%" height="16px" color={token('color.skeleton.subtle')} />
				<Skeleton width="100%" height="16px" color={token('color.skeleton.subtle')} />
				<Skeleton width="90%" height="16px" color={token('color.skeleton.subtle')} />
			</Stack>
		</AnimatedWrapper>
	);
}
